import { apiFetch, ApiError, OfflineError, newIdempotencyKey } from './api';
import { db, readCache, writeCache, type OutboxEntry } from './db';
import { onlineStore } from './onlineStore';

// PWA-002/PWA-007: gecachte GET-Antwort anzeigen, wenn offline oder die
// Anfrage fehlschlägt; sonst frische Daten holen und den Cache aktualisieren.
export async function cachedGet<T>(cacheKey: string, path: string): Promise<{ data: T; fromCache: boolean }> {
  if (!onlineStore.get()) {
    const cached = await readCache<T>(cacheKey);
    if (cached !== undefined) return { data: cached, fromCache: true };
    throw new OfflineError();
  }
  try {
    const data = await apiFetch<T>(path);
    await writeCache(cacheKey, data);
    return { data, fromCache: false };
  } catch (err) {
    if (err instanceof OfflineError) {
      const cached = await readCache<T>(cacheKey);
      if (cached !== undefined) return { data: cached, fromCache: true };
    }
    throw err;
  }
}

// PWA-003/004/005/SYNC-001..003: schreibende Aktion sofort versuchen; bei
// fehlender Verbindung dauerhaft in der Outbox einreihen und später mit
// demselben Idempotency-Key wiederholen (keine doppelten Käufe/Preise).
export async function queuedMutation<T>(opts: {
  method: 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  path: string;
  body?: unknown;
  entityType: string;
  meta?: unknown;
}): Promise<{ result?: T; queued: boolean }> {
  const idempotencyKey = newIdempotencyKey();
  const clientTimestamp = new Date().toISOString();
  if (onlineStore.get()) {
    try {
      const result = await apiFetch<T>(opts.path, {
        method: opts.method,
        body: opts.body ? JSON.stringify(opts.body) : undefined,
        idempotencyKey,
        clientTimestamp,
      });
      return { result, queued: false };
    } catch (err) {
      if (!(err instanceof OfflineError)) throw err; // echter Fachfehler -> nicht queuen, sofort melden
    }
  }
  await db.outbox.put({
    id: idempotencyKey,
    method: opts.method,
    path: opts.path,
    body: opts.body,
    createdAt: Date.now(),
    entityType: opts.entityType,
    meta: opts.meta,
    status: 'pending',
    attempts: 0,
  });
  return { queued: true };
}

// Noch nicht synchronisierte Änderungen, älteste zuerst - damit die Oberfläche
// lokale Änderungen auch dann anzeigen kann, wenn der Server (noch) nichts davon weiß.
export async function pendingOutboxEntries(): Promise<OutboxEntry[]> {
  return db.outbox.where('status').equals('pending').sortBy('createdAt');
}

let syncing = false;

// PWA-006: nach Wiederverbindung automatisch und wiederholbar synchronisieren.
export async function processOutbox(): Promise<{ applied: number; failed: number }> {
  if (syncing || !onlineStore.get()) return { applied: 0, failed: 0 };
  syncing = true;
  let applied = 0;
  let failed = 0;
  try {
    const entries = await db.outbox.where('status').equals('pending').sortBy('createdAt');
    for (const entry of entries) {
      try {
        await apiFetch(entry.path, {
          method: entry.method,
          body: entry.body ? JSON.stringify(entry.body) : undefined,
          idempotencyKey: entry.id,
          clientTimestamp: new Date(entry.createdAt).toISOString(),
        });
        await db.outbox.delete(entry.id);
        applied += 1;
      } catch (err) {
        if (err instanceof OfflineError) break; // Verbindung erneut weg - später weiterversuchen
        failed += 1;
        // SYNC-006/SYNC-007: fehlgeschlagene Änderung bleibt sichtbar (nicht stillschweigend verworfen).
        const message = err instanceof ApiError ? `${err.code}: ${err.message}` : String(err);
        await db.outbox.update(entry.id, { status: 'failed', lastError: message, attempts: entry.attempts + 1 });
      }
    }
  } finally {
    syncing = false;
  }
  return { applied, failed };
}

export async function discardFailedOutboxEntry(id: string): Promise<void> {
  await db.outbox.delete(id);
}

export async function retryOutboxEntry(id: string): Promise<void> {
  await db.outbox.update(id, { status: 'pending' });
  await processOutbox();
}

window.addEventListener('online', () => {
  void processOutbox();
});
